import { api, ApiError } from './client'
import type { LoginResponse } from './auth'

/**
 * Kit-code auth: el código impreso en el kit es la AUTENTICACIÓN DEL
 * DISPOSITIVO con el club (decisión de mg, 2026-09-07). Cada club recibe un
 * kit físico con un código y una tablet adentro; el dispositivo queda ligado a
 * ese kit (`kit/kitAccessStore.ts`, `kit/useKitBinding.ts`) y recién entonces
 * los jugadores entran de a uno con su PIN de 6 dígitos — no hay roster ni
 * email/password que tipear en un dispositivo compartido. Lo usan el modo
 * tablet (`/tablet`), la entrada de `/login` y el visor (vr-app).
 *
 * Contrato espejado del vr-app (`vr-app/src/api/kitAuth.ts`) con dos
 * diferencias: `/auth/kit/verify` ya NO devuelve el roster de jugadores (el
 * PIN es lo único que identifica), y `/auth/kit/login` devuelve además
 * `display_name` + `gates` para que la tablet sepa qué pantalla mostrar
 * después del login sin una llamada extra.
 */

export interface KitVerifyClub {
  id: number
  name: string
  logo_url: string | null
  primary_color: string | null
  secondary_color: string | null
  accent_color: string | null
  /**
   * Si el club tiene el servicio VR vigente (`club.service_until`). Opcional
   * en el tipo para tolerar un backend anterior a 2026-09-07: sólo un `false`
   * explícito suspende el dispositivo (`kit/useKitBinding.ts`).
   */
  service_active?: boolean
}

export interface KitVerifyResponse {
  kit: { id: number; label: string }
  club: KitVerifyClub
}

/**
 * Qué le falta al jugador que acaba de entrar, resuelto por el backend en la
 * misma respuesta del login. `questionnaire_pending` es el PRIMER pendiente
 * (code + título legible), no la lista entera: la tablet manda uno por
 * sesión y desloguea al terminarlo.
 */
export interface KitGates {
  consent_required: boolean
  questionnaire_pending: { code: string; title: string } | null
}

export interface KitLoginResponse extends LoginResponse {
  display_name: string
  gates: KitGates
}

/**
 * 404 (ApiError) cuando el código no matchea un kit activo; 429 con
 * `retry_after_s` cuando el throttling por IP corta los intentos (mismo
 * contrato que `/auth/kit/login` — ver `kitRetryAfterSeconds`).
 */
export function verifyKitCode(code: string): Promise<KitVerifyResponse> {
  return api.post<KitVerifyResponse>('/auth/kit/verify', { code })
}

/**
 * El PIN identifica al jugador dentro del club del kit (no viaja user_id).
 * 401 genérico ("PIN incorrecto.") en PIN equivocado — no distingue "no
 * existe" de "no coincide" —, 429 con `retry_after_s` cuando el throttling
 * corta los intentos. Setea la cookie de sesión igual que `/auth/login`, así
 * que el caller sólo tiene que llamar a `refresh()` del AuthContext.
 */
export function kitLogin(code: string, pin: string): Promise<KitLoginResponse> {
  return api.post<KitLoginResponse>('/auth/kit/login', { code, pin })
}

/**
 * Reset de PIN desde la tablet: el jugador tipea su email y el backend le
 * manda el link de `/set-pin`. Siempre 200 — nunca revela si ese email es
 * de un jugador del club.
 */
export function requestKitPinResetByEmail(code: string, email: string): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/auth/pin/reset-request-by-email', { code, email })
}

/**
 * Segundos de espera de un 429 de los endpoints de kit (`/auth/kit/login` y
 * `/auth/kit/verify`, mismo contrato). El backend puede mandarlo
 * plano (`{retry_after_s}`) o adentro del `detail` de FastAPI, así que se
 * miran los dos; `null` si el error no es un throttling o no trae el dato
 * (el caller cae a una espera genérica en vez de mostrar un contador falso).
 */
export function kitRetryAfterSeconds(e: unknown): number | null {
  if (!(e instanceof ApiError) || e.status !== 429) return null
  const direct = readRetryAfter(e.body)
  if (direct !== null) return direct
  const detail = e.body && typeof e.body === 'object' ? (e.body as { detail?: unknown }).detail : null
  return readRetryAfter(detail)
}

function readRetryAfter(o: unknown): number | null {
  if (!o || typeof o !== 'object' || !('retry_after_s' in o)) return null
  const v = (o as { retry_after_s: unknown }).retry_after_s
  if (typeof v !== 'number' || !Number.isFinite(v)) return null
  return Math.max(0, Math.ceil(v))
}
